import CheckboxInput from 'components/forms/inputs/rhf-inputs/CheckboxInput';
import DateInput from 'components/forms/inputs/rhf-inputs/DateInput';
import SelectInput from 'components/forms/inputs/rhf-inputs/SelectInput';
import TextAreaInput from 'components/forms/inputs/rhf-inputs/TextAreaInput';
import TextInput from 'components/forms/inputs/rhf-inputs/TextInput';
import { customizationAfterTemplatePick } from 'pages/invoices/layout/views/general-tab/supportedCustomization';
import { useCallback } from 'react';
import { CustomizationDto } from 'types/invoices';
import { FormattedMessage } from 'react-intl';
import {
    useLazyGetMinContactsListWithFiltersQuery,
    useLazyGetMinContactsWithRelationsListWithFiltersQuery,
} from 'store/api/contacts';
import { useLazyGetEmployeesListWithFiltersQuery } from 'store/api/employees';
import { useLazyGetMinOfficesWithFiltersQuery } from 'store/api/offices';
import { PartyType } from 'types/contacts';
import { NamedEntityMinDto } from 'types/other';
import { getValidOptionsFromClassifier } from 'utils/formUtil';
import { t } from 'utils/getTranslations';
import { officeInactiveSuffix } from 'utils/officeOptionRenderer';
import Tooltip from 'components/@extended/Tooltip';
import PermissionButton from 'components/buttons/PermissionButton';
import FeatureFlagged from 'components/featureflag/FeatureFlagged';
import ContactPickerModal from 'components/forms/form-components/select-picker/ContactPickerModal.tsx';
import DynamicMultiSelectInput from 'components/forms/inputs/rhf-inputs/DynamicMultiSelectInput';
import DynamicSelectInput from 'components/forms/inputs/rhf-inputs/DynamicSelectInput';
import InfoTooltip from 'components/InfoTooltip';
import ContactDynamicMultiSelectPicker from 'components/party/ContactDynamicMultiSelectPicker';
import ContactDynamicSelectPicker from 'components/party/ContactDynamicSelectPicker';
import { useInRecordSection } from 'components/section-edit/SectionEdit';
import { FeatureFlag } from 'types/features';
import { MatterPayersFields } from './MatterPayersFields';
import { MatterFormState } from './useMatterForm';

/** Either lets a user customise a case's invoice and specification, from the button under the invoice template. */
export const CUSTOMIZE_INVOICE_PERMISSIONS = [
    'READ.CUSTOMIZE_INVOICE_SPEC_TEMPLATE',
    'UPDATE.CUSTOMIZE_INVOICE_SPEC_TEMPLATE',
];

/** Invoice specification: language, recipients, payer, templates, the next invoicing date and timecard blocking. */
export const InvoiceSpecFields = ({ form }: { form: MatterFormState }): JSX.Element => {
    const { control, setValue, getValues, formState } = form.methods;
    const {
        changeReasonFields,
        classifiers,
        getOptionLabelWithPartyNumberAndEmail,
        groupInvoiceRecipients,
        invoiceRecipientQueryCompanyIds,
        invoiceSpecTemplates,
        invoiceTemplates,
        isAdministrativeMatter,
        isConfidential,
        isLoadingNewClientData,
        isSharedPaymentEnabled,
        isSpecIncludedInInvoice,
        isUserPrivilegedToUpdateBlockedFields,
        officeInvoiceTemplates,
        officeSpecTemplates,
        openAddNewContactModal,
        openContactTypePicker,
        openCustomizeInvoiceModal,
    } = form;
    // On the record page each field is a cell of the Invoice specification card's grid, where the read view has
    // its row: what the form draws around a field shares that field's cell.
    const inSection = useInRecordSection();
    const savedTemplates = formState.defaultValues;
    const resetInvoiceCustomization = useCallback(() => {
        const customization = customizationAfterTemplatePick(
            {
                invoiceTemplateId: getValues('invoiceTemplate')?.id,
                invoiceSpecTemplateId: getValues('invoiceSpecTemplate')?.id,
            },
            {
                invoiceTemplateId: savedTemplates?.invoiceTemplate?.id,
                invoiceSpecTemplateId: savedTemplates?.invoiceSpecTemplate?.id,
                invoiceCustomization: savedTemplates?.invoiceCustomization as CustomizationDto | null | undefined,
            }
        );
        setValue('invoiceCustomization', customization, { shouldDirty: true });
    }, [getValues, savedTemplates, setValue]);

    const invoiceTemplateField = (
        <SelectInput
            setValue={setValue}
            control={control}
            label="matters.invoice-template"
            placeholder="matters.select-invoice-template"
            name="invoiceTemplate"
            options={
                (officeInvoiceTemplates?.length
                    ? officeInvoiceTemplates
                    : invoiceTemplates?.filter((it) => !it.isCorrection) || []) as NamedEntityMinDto[]
            }
            getOptionLabel="name"
            getOptionIdentifier="id"
            disabled={isLoadingNewClientData}
            onChange={resetInvoiceCustomization}
        />
    );
    const customizeButton = (
        <FeatureFlagged action="show" flag={FeatureFlag.USE_CUSTOMIZED_INVOICE_TEMPLATE}>
            <div className={inSection ? 'empty:hidden' : 'mt-4 flex justify-start'}>
                <PermissionButton
                    className={inSection ? undefined : 'mt-2'}
                    size="extraSmall"
                    variant="outlined"
                    onClick={openCustomizeInvoiceModal}
                    permissions={CUSTOMIZE_INVOICE_PERMISSIONS}
                >
                    <FormattedMessage id="invoices.custom.customize" />
                </PermissionButton>
            </div>
        </FeatureFlagged>
    );

    return (
        <>
            <SelectInput
                setValue={setValue}
                control={control}
                label="matters.timecard-language"
                placeholder="matters.select-timecard-language"
                name="timecardLanguage"
                options={
                    (getValidOptionsFromClassifier(classifiers['LANGUAGE']).filter(
                        (option) => !option.parameters || option.parameters['hideForTimecard'] !== true
                    ) as {
                        label: string;
                        value: string;
                    }[]) ?? []
                }
                getOptionLabel="label"
                getOptionIdentifier="value"
                required={!isAdministrativeMatter}
            />
            <DynamicSelectInput
                setValue={setValue}
                control={control}
                label="matters.invoiced-by-office"
                placeholder="matters.select-office"
                name="invoicedByOffice"
                paginated={{
                    fetch: useLazyGetMinOfficesWithFiltersQuery,
                    searchField: 'name',
                    filters: 'isActive!=false',
                }}
                getOptionLabel="name"
                getOptionIdentifier="id"
                getOptionEndAdornment={officeInactiveSuffix}
                required={!isAdministrativeMatter}
            />
            <ContactDynamicSelectPicker
                setValue={setValue}
                control={control}
                label="matters.primary-recipient"
                placeholder="matters.select-primary-recipient"
                name="primaryRecipientParty"
                tooltip={<InfoTooltip text="matters.tooltips.primary-recipient" />}
                PickerModalComponent={ContactPickerModal}
                paginated={{
                    fetch: invoiceRecipientQueryCompanyIds.length
                        ? useLazyGetMinContactsWithRelationsListWithFiltersQuery
                        : useLazyGetMinContactsListWithFiltersQuery,
                    searchField: ['name', 'partyNumber'],
                    filters: `partyType==${PartyType.PERSON}`,
                    additionalParams: invoiceRecipientQueryCompanyIds.length
                        ? { prioritizeEmployeesOf: invoiceRecipientQueryCompanyIds }
                        : undefined,
                }}
                {...(invoiceRecipientQueryCompanyIds.length && {
                    groupByStaticFields: groupInvoiceRecipients,
                })}
                isPickerModalOptionEnabled={(option) => option.partyType === PartyType.PERSON}
                groupSortOrder={[t('efforts.groups.company-related')]}
                getOptionLabel={getOptionLabelWithPartyNumberAndEmail}
                noOptionsContent={
                    <PermissionButton
                        permissions={[
                            'CREATE.PARTIES',
                            'CREATE.PARTIES.CLIENTS',
                            'CREATE.PARTIES.LEADS_AND_NON_CLIENTS',
                        ]}
                        variant="contained"
                        color="secondary"
                        size="extraSmall"
                        onClick={() => openAddNewContactModal('primaryRecipientParty', true)}
                    >
                        <FormattedMessage id="contacts.add-new-contact" />
                    </PermissionButton>
                }
            />
            <TextAreaInput
                control={control}
                label="matters.primary-recipient-emails"
                placeholder="matters.primary-recipient-emails"
                name="primaryRecipientAdditionalEmails"
                tooltip={<InfoTooltip text="matters.tooltips.primary-recipient-emails" />}
                validate={validateEmails}
                onBlur={removeEmptyStringsFromEmailList}
                disabled={isLoadingNewClientData}
            />
            <ContactDynamicMultiSelectPicker
                setValue={setValue}
                control={control}
                label="matters.secondary-recipient"
                placeholder="matters.select-secondary-recipient"
                name="secondaryRecipientParties"
                tooltip={<InfoTooltip text="matters.tooltips.secondary-recipient" />}
                PickerModalComponent={ContactPickerModal}
                paginated={{
                    fetch: invoiceRecipientQueryCompanyIds.length
                        ? useLazyGetMinContactsWithRelationsListWithFiltersQuery
                        : useLazyGetMinContactsListWithFiltersQuery,
                    searchField: ['name', 'partyNumber'],
                    filters: `partyType==${PartyType.PERSON}`,
                    additionalParams: invoiceRecipientQueryCompanyIds.length
                        ? { prioritizeEmployeesOf: invoiceRecipientQueryCompanyIds }
                        : undefined,
                }}
                {...(invoiceRecipientQueryCompanyIds.length && {
                    groupByStaticFields: groupInvoiceRecipients,
                })}
                isPickerModalOptionEnabled={(option) => option.partyType === PartyType.PERSON}
                groupSortOrder={[t('efforts.groups.company-related')]}
                getOptionLabel={getOptionLabelWithPartyNumberAndEmail}
                noOptionsContent={
                    <PermissionButton
                        permissions={[
                            'CREATE.PARTIES',
                            'CREATE.PARTIES.CLIENTS',
                            'CREATE.PARTIES.LEADS_AND_NON_CLIENTS',
                        ]}
                        variant="contained"
                        color="secondary"
                        size="extraSmall"
                        onClick={() => openAddNewContactModal('secondaryRecipientParties', true)}
                    >
                        <FormattedMessage id="contacts.add-new-contact" />
                    </PermissionButton>
                }
                disabled={isLoadingNewClientData}
            />
            <TextAreaInput
                control={control}
                label="matters.secondary-recipient-emails"
                placeholder="matters.secondary-recipient-emails"
                name="secondaryRecipientAdditionalEmails"
                tooltip={<InfoTooltip text="matters.tooltips.secondary-recipient-emails" />}
                validate={validateEmails}
                onBlur={removeEmptyStringsFromEmailList}
                disabled={isLoadingNewClientData}
            />
            <TextInput
                name="matterClientReference"
                control={control}
                label="matters.client-reference"
                placeholder="matters.client-reference"
            />
            <TextInput
                name="invoiceMatterName"
                control={control}
                label="matters.name-on-invoice"
                placeholder="matters.name-on-invoice"
                required
            />
            <FeatureFlagged
                action="show"
                mode="any"
                flag={[FeatureFlag.LOCALIZED_FIELDS, FeatureFlag.LOCALIZED_FIELDS_CASES]}
            >
                <TextInput
                    name="invoiceMatterNameLocalLanguage"
                    control={control}
                    label="matters.name-on-invoice-local"
                    placeholder="matters.name-on-invoice-local"
                />
            </FeatureFlagged>
            {isSharedPaymentEnabled ? (
                <MatterPayersFields form={form} />
            ) : (
                <ContactDynamicSelectPicker
                    setValue={setValue}
                    control={control}
                    label="matters.payer"
                    placeholder="matters.select-payer"
                    name="payerParty"
                    PickerModalComponent={ContactPickerModal}
                    paginated={{
                        fetch: useLazyGetMinContactsListWithFiltersQuery,
                        searchField: ['name', 'partyNumber'],
                    }}
                    noOptionsContent={
                        <PermissionButton
                            permissions={[
                                'CREATE.PARTIES',
                                'CREATE.PARTIES.CLIENTS',
                                'CREATE.PARTIES.LEADS_AND_NON_CLIENTS',
                            ]}
                            variant="contained"
                            color="secondary"
                            size="extraSmall"
                            onClick={() => openContactTypePicker('payerParty')}
                        >
                            <FormattedMessage id="contacts.add-new-contact" />
                        </PermissionButton>
                    }
                    disabled={isLoadingNewClientData}
                />
            )}
            {inSection ? (
                // One cell: the button has the line under the select, which the read row keeps free for it.
                <div className="form-field flex flex-col items-start gap-1">
                    {invoiceTemplateField}
                    {customizeButton}
                </div>
            ) : (
                <>
                    {invoiceTemplateField}
                    {customizeButton}
                </>
            )}
            <SelectInput
                setValue={setValue}
                control={control}
                label="matters.invoice-specification-template"
                placeholder="matters.select-invoice-specification-template"
                name="invoiceSpecTemplate"
                options={
                    (officeSpecTemplates?.length
                        ? officeSpecTemplates
                        : invoiceSpecTemplates || []) as NamedEntityMinDto[]
                }
                getOptionLabel="name"
                getOptionIdentifier="id"
                disabled={isLoadingNewClientData}
                onChange={resetInvoiceCustomization}
            />
            <CheckboxInput
                control={control}
                name="isSpecIncludedInInvoice"
                label="invoices.general-tab.details.merge-invoice-and-spec"
                onChange={(checked) => {
                    if (checked) {
                        setValue('isSpecExcludedFromEmail', false);
                    }
                }}
            />
            <CheckboxInput
                control={control}
                name="isSpecExcludedFromEmail"
                label="invoices.general-tab.details.exclude-spec-pdf-attachment-in-email"
                disabled={isSpecIncludedInInvoice}
            />
            <DateInput control={control} name="nextInvoicingDate" label="matters.next-invoicing-date" />
            <DynamicMultiSelectInput
                setValue={setValue}
                control={control}
                label="contacts.billing.invoice-payment-notification-to"
                name="paymentNotificationReceivers"
                paginated={{
                    fetch: useLazyGetEmployeesListWithFiltersQuery,
                    searchField: 'fullName',
                }}
                getOptionLabel="name"
                getOptionIdentifier="id"
                placeholder="contacts.billing.invoice-payment-notification-to"
            />
            {isConfidential ? (
                <Tooltip text="matters.no-joint-invoice-confidential-tooltip">
                    {/* The field's own cell on the record page. */}
                    <span className={inSection ? 'form-field' : 'inline-block w-fit'}>
                        <CheckboxInput
                            control={control}
                            name="isJointInvoicingDisabled"
                            label="matters.no-joint-invoice"
                            disabled={true}
                        />
                    </span>
                </Tooltip>
            ) : (
                <CheckboxInput control={control} name="isJointInvoicingDisabled" label="matters.no-joint-invoice" />
            )}
            <SelectInput
                setValue={setValue}
                control={control}
                label="matters.timecard-blocking-policy"
                placeholder="matters.timecard-blocking-policy"
                name="matterBlockingPolicyType"
                options={
                    (getValidOptionsFromClassifier(classifiers['MATTER_BLOCKING_POLICY_TYPE']) as {
                        label: string;
                        value: string;
                    }[]) ?? []
                }
                getOptionLabel="label"
                getOptionIdentifier="value"
                required
                disabled={!isUserPrivilegedToUpdateBlockedFields}
                withChangeReason={changeReasonFields.matterBlockingPolicyType}
            />
            <CheckboxInput
                control={control}
                name="isAmlBlockingExempt"
                label="matters.excluded-from-aml-blocking"
                disabled={!isUserPrivilegedToUpdateBlockedFields}
                withChangeReason={changeReasonFields.isAmlBlockingExempt}
            />
        </>
    );
};

// Validate if email field matches email pattern
// Signature accepts `unknown` to remain compatible with RHF's `Validate<unknown, T>` shape
// used across MatterForm / ContactForm fields; narrowed to string inside the body.
export const validateEmails = (value: unknown) => {
    if (typeof value !== 'string' || !value) {
        return Promise.resolve(true);
    }

    const emails = value.split(new RegExp('[\\s,;]+'));
    const pattern = new RegExp('^[a-zA-Z0-9_+&*-]+(?:\\.[a-zA-Z0-9_+&*-]+)*@(?:[a-zA-Z0-9-]+\\.)+[a-zA-Z]{2,7}$');

    if (emails.length && emails.find((el: string) => !pattern.test(el))) {
        return Promise.resolve(t('validation.email-must-be-valid'));
    }

    return Promise.resolve(true);
};

// Improved trim() function removes all undefined or empty emails from list
export const removeEmptyStringsFromEmailList = (value: string | null) => {
    if (!value) {
        return value;
    }

    const emails = value.split(new RegExp('[\\s,;]+')).filter((el) => !!el);
    return emails.join(', ');
};
